// Session and company context: who is signed in, the company settings the whole
// app reads (tax rate, currency, logo), and sign in / sign out.

import {
  createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode,
} from 'react';
import { api, setUnauthorizedHandler, type SessionInfo } from './api';
import { useI18n } from './i18n';
import { moneyFormatFromCurrency, type MoneyFormat } from '@shared/format';
import type { Currency, Locale, User } from '@shared/types';

interface SessionValue {
  status: 'loading' | 'authenticated' | 'anonymous';
  user: User | undefined;
  company: SessionInfo['company'] | undefined;
  taxRate: number;
  currency: Currency | undefined;
  money: MoneyFormat;
  /** Returns what the second step must be; never completes sign-in on its own. */
  signIn: (username: string, password: string) => Promise<'totp' | 'enrol'>;
  /** Completes sign-in after the second step has been satisfied. */
  completeSignIn: () => Promise<void>;
  signOut: () => Promise<void>;
  /** Re-reads /auth/me and the currency list, after Settings changes. */
  refresh: () => Promise<void>;
  setUserLocale: (locale: Locale) => Promise<void>;
}

const SessionContext = createContext<SessionValue | undefined>(undefined);

export function SessionProvider({ children }: { children: ReactNode }) {
  const { setLocale } = useI18n();
  const [status, setStatus] = useState<SessionValue['status']>('loading');
  const [info, setInfo] = useState<SessionInfo>();
  const [currencies, setCurrencies] = useState<Currency[]>([]);

  const load = useCallback(async (applyLocale: boolean) => {
    const session = await api.session();
    setInfo(session);
    setStatus('authenticated');
    if (applyLocale) setLocale(session.user.locale);
    try {
      setCurrencies(await api.currencies());
    } catch {
      // Currency list is cosmetic; a failure must not block the app.
    }
  }, [setLocale]);

  // Restore an existing session on first paint.
  useEffect(() => {
    load(true).catch(() => setStatus('anonymous'));
  }, [load]);

  // A 401 from any call means the cookie expired or was revoked.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      setInfo(undefined);
      setStatus('anonymous');
    });
  }, []);

  // A correct password yields a challenge, not a session: the caller must go
  // through the second step before `completeSignIn` will find anything.
  const signIn = useCallback(async (username: string, password: string) => {
    const challenge = await api.login(username, password);
    setLocale(challenge.locale);
    return challenge.stage;
  }, [setLocale]);

  const completeSignIn = useCallback(() => load(true), [load]);

  const signOut = useCallback(async () => {
    try { await api.logout(); } finally {
      setInfo(undefined);
      setStatus('anonymous');
    }
  }, []);

  const setUserLocale = useCallback(async (locale: Locale) => {
    setLocale(locale);
    try {
      await api.setMyLocale(locale);
      setInfo((prev) => (prev ? { ...prev, user: { ...prev.user, locale } } : prev));
    } catch {
      // The preference still applies for this session even if it can't be saved.
    }
  }, [setLocale]);

  const currency = useMemo(
    () => currencies.find((c) => c.code === info?.currencyCode),
    [currencies, info?.currencyCode]);

  const value = useMemo<SessionValue>(() => ({
    status,
    user: info?.user,
    company: info?.company,
    taxRate: info?.taxRate ?? 0,
    currency,
    money: moneyFormatFromCurrency(currency),
    signIn,
    completeSignIn,
    signOut,
    refresh: () => load(false),
    setUserLocale,
  }), [status, info, currency, signIn, completeSignIn, signOut, load, setUserLocale]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used inside <SessionProvider>');
  return ctx;
}
